import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, CircularProgress, Tooltip } from "@mui/material";
import { Mic } from "@mui/icons-material";
import { useIntl } from "react-intl";
import api from "../../api";

// Utility (mirrors SurveyPreview) to render colour objects/strings as CSS
const toCss = (color, fallback = "rgba(0, 0, 0, 0.6)") => {
  if (!color) return fallback;
  if (typeof color === "string") return color;
  if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
    const alpha = color.a !== undefined ? color.a : 1;
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
  }
  return fallback;
};

// Pick a MediaRecorder mime type, preferring webm/opus.
const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  const candidates = ["audio/webm;codecs=opus", "audio/webm"];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
};

// Ignore recordings shorter than this — too short to hold real speech.
const MIN_DURATION_MS = 700;

// Live volume meter tuning
const BAR_COUNT = 20;
const BAR_SMOOTHING = 0.35;

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

/**
 * Record-and-transcribe control for open-text survey answers.
 *
 * Records mic audio via the MediaRecorder API, POSTs the resulting webm blob to
 * the token-gated collector transcribe endpoint, and hands the returned
 * transcript back to the parent via onTranscript. The BE auto-detects the
 * language, so the client sends only audio.
 *
 * A live volume meter reflects the analyser data while recording (purely
 * visual — silence filtering is handled server-side).
 *
 * Audio is never persisted — the blob is discarded as soon as the request
 * resolves. Never auto-submits.
 */
function VoiceTranscription({ settings = {}, onTranscript, disabled = false, surveyId, token }) {
  const intl = useIntl();
  // "idle" | "recording" | "transcribing"
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);

  // Web Audio analyser — drives the live volume meter only.
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  // Live meter
  const canvasRef = useRef(null);
  const barLevelsRef = useRef(new Array(BAR_COUNT).fill(0));

  const textColor = toCss(settings.fontColour, "rgba(0, 0, 0, 0.6)");
  const meterColor = toCss(settings.buttonColour || settings.accentColor || settings.fontColour, "#d32f2f");

  // Stop any live mic tracks — belt-and-suspenders cleanup so the mic light
  // never stays on after we're done recording.
  const stopStreamTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopLoudnessMonitor = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        /* no-op */
      }
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  // Draw the iPhone-style volume meter from the analyser's frequency data.
  const drawMeter = useCallback(
    (analyser, freqBuf) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth || 120;
      const cssH = canvas.clientHeight || 28;
      const targetW = Math.round(cssW * dpr);
      const targetH = Math.round(cssH * dpr);
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      analyser.getByteFrequencyData(freqBuf);

      // Voice energy lives in the lower part of the spectrum — sample that.
      const usableBins = Math.max(BAR_COUNT, Math.floor(freqBuf.length * 0.5));
      const per = Math.floor(usableBins / BAR_COUNT);
      const gap = 2;
      const barW = Math.max(2, (cssW - gap * (BAR_COUNT - 1)) / BAR_COUNT);
      const minH = 2;
      const levels = barLevelsRef.current;

      ctx.fillStyle = meterColor;
      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        for (let j = 0; j < per; j++) sum += freqBuf[i * per + j];
        const target = sum / per / 255; // 0..1
        // Smooth toward the target so motion is fluid, not jittery.
        const lvl = levels[i] + (target - levels[i]) * BAR_SMOOTHING;
        levels[i] = lvl;

        const h = Math.max(minH, lvl * cssH);
        const x = i * (barW + gap);
        const y = (cssH - h) / 2;
        ctx.globalAlpha = 0.5 + 0.5 * Math.min(1, lvl);
        ctx.fillRect(x, y, barW, h);
      }
      ctx.globalAlpha = 1;
    },
    [meterColor]
  );

  // Per-frame loop: draws the live volume meter from the analyser data.
  const monitorLoudness = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const freqBuf = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      const a = analyserRef.current;
      if (!a) return;
      drawMeter(a, freqBuf);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [drawMeter]);

  // Elapsed-time counter while recording
  useEffect(() => {
    if (status !== "recording") return undefined;
    const id = setInterval(() => setElapsedMs(Date.now() - startTimeRef.current), 250);
    return () => clearInterval(id);
  }, [status]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        /* no-op */
      }
      stopStreamTracks();
      stopLoudnessMonitor();
    };
  }, [stopStreamTracks, stopLoudnessMonitor]);

  const transcribe = useCallback(
    async (blob) => {
      setStatus("transcribing");
      setError("");
      try {
        const data = await api.transcribeAudio(blob, surveyId, token);
        const text =
          data?.text ?? data?.transcript ?? data?.data?.text ?? data?.data?.transcript ?? "";
        if (typeof text === "string" && text.trim()) {
          onTranscript(text.trim());
        } else {
          // BE returned an empty transcript — handle gracefully, append nothing.
          setError(
            intl.formatMessage({
              id: "survey.voice.empty_transcript",
              defaultMessage: "Didn't catch that — please try again.",
            })
          );
        }
      } catch (e) {
        setError(
          intl.formatMessage({
            id: "survey.voice.transcribe_failed",
            defaultMessage: "Transcription failed. Please try again or type your answer.",
          })
        );
      } finally {
        // Discard the audio: the blob only lived as a local argument, and chunks
        // are cleared here so nothing is retained in state.
        chunksRef.current = [];
        setStatus("idle");
      }
    },
    [onTranscript, intl, surveyId, token]
  );

  const startRecording = useCallback(async () => {
    setError("");
    setElapsedMs(0);

    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError(
        intl.formatMessage({
          id: "survey.voice.unsupported",
          defaultMessage: "Voice recording isn't supported in this browser. Please type your answer.",
        })
      );
      return;
    }

    let stream;
    try {
      // Noise suppression on, auto-gain off (AGC inflates silence toward the gate).
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
        },
      });
    } catch (e) {
      setError(
        intl.formatMessage({
          id: "survey.voice.permission_denied",
          defaultMessage: "Microphone access was blocked. Please allow the mic and try again.",
        })
      );
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    // Reset meter state for this take.
    barLevelsRef.current = new Array(BAR_COUNT).fill(0);

    // Set up the analyser for the volume meter (non-fatal if Web Audio is unavailable).
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser); // not connected to destination — no echo
        audioContextRef.current = ctx;
        analyserRef.current = analyser;
        monitorLoudness();
      }
    } catch (e) {
      /* no-op — meter is optional */
    }

    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (e) {
      stopStreamTracks();
      stopLoudnessMonitor();
      setError(
        intl.formatMessage({
          id: "survey.voice.unsupported",
          defaultMessage: "Voice recording isn't supported in this browser. Please type your answer.",
        })
      );
      return;
    }

    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      stopStreamTracks();
      stopLoudnessMonitor();

      const duration = Date.now() - startTimeRef.current;
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];

      if (!blob.size) {
        setStatus("idle");
        setError(
          intl.formatMessage({
            id: "survey.voice.no_audio",
            defaultMessage: "No audio was captured. Please try recording again.",
          })
        );
        return;
      }

      // Reject too-short takes; otherwise send. Silence filtering is server-side.
      if (duration < MIN_DURATION_MS) {
        setStatus("idle");
        setError(
          intl.formatMessage({
            id: "survey.voice.too_short",
            defaultMessage: "Recording too short, please try again.",
          })
        );
        return;
      }

      transcribe(blob);
    };

    startTimeRef.current = Date.now();
    recorder.start();
    setStatus("recording");
  }, [intl, stopStreamTracks, stopLoudnessMonitor, monitorLoudness, transcribe]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop(); // fires onstop → guards → transcribe
    }
  }, []);

  const isRecording = status === "recording";
  const isTranscribing = status === "transcribing";

  const tooltipTitle = isTranscribing
    ? intl.formatMessage({ id: "survey.voice.transcribing", defaultMessage: "Transcribing…" })
    : isRecording
      ? intl.formatMessage({ id: "survey.voice.stop", defaultMessage: "Stop recording" })
      : intl.formatMessage({ id: "survey.voice.start", defaultMessage: "Start recording" });

  let control;
  if (isTranscribing) {
    control = (
      <Box
        role="status"
        aria-label={intl.formatMessage({ id: "survey.voice.transcribing", defaultMessage: "Transcribing…" })}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
        }}
      >
        <CircularProgress size={18} sx={{ color: textColor }} />
      </Box>
    );
  } else if (isRecording) {
    control = (
      <IconButton
        onClick={stopRecording}
        size="small"
        aria-label={intl.formatMessage({ id: "survey.voice.stop", defaultMessage: "Stop recording" })}
        sx={{
          border: "1px solid #d32f2f",
          borderRadius: "8px",
          color: "#d32f2f",
          animation: "prompta-rec-pulse 1.2s ease-in-out infinite",
          "@keyframes prompta-rec-pulse": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.4 },
            "100%": { opacity: 1 },
          },
          "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
        }}
      >
        <Mic fontSize="small" />
      </IconButton>
    );
  } else {
    control = (
      <IconButton
        onClick={startRecording}
        disabled={disabled}
        size="small"
        aria-label={intl.formatMessage({ id: "survey.voice.start", defaultMessage: "Start recording" })}
        sx={{
          border: `1px solid ${textColor}`,
          borderRadius: "8px",
          color: textColor,
          opacity: 0.85,
          "&:hover": { opacity: 1, backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <Mic fontSize="small" />
      </IconButton>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Tooltip title={tooltipTitle}>
          {/* span wrapper keeps the Tooltip working even when the button is disabled */}
          <span style={{ display: "inline-flex" }}>{control}</span>
        </Tooltip>

        {isRecording && (
          <>
            <Box
              component="canvas"
              ref={canvasRef}
              aria-hidden="true"
              sx={{
                width: 120,
                height: 28,
                display: "block",
              }}
            />
            <Typography
              sx={{ fontSize: "0.8rem", color: "#d32f2f", fontVariantNumeric: "tabular-nums" }}
            >
              {formatElapsed(elapsedMs)}
            </Typography>
          </>
        )}

        {isTranscribing && (
          <Typography sx={{ fontSize: "0.8rem", color: textColor }}>
            {intl.formatMessage({ id: "survey.voice.transcribing", defaultMessage: "Transcribing…" })}
          </Typography>
        )}
      </Box>

      {error && (
        <Typography sx={{ mt: 0.75, fontSize: "0.8rem", color: "#d32f2f" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default VoiceTranscription;
